import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Form, Formik } from 'formik'

import Text from '@components/kit/text'
import Box from '@components/kit/box'
import Loading from '@components/kit/loading'
import Button from '@components/kit/button'
import ScrollView from '@components/kit/scroll-view'
import Modal from '@components/kit/modal'
import Pressable from '@components/kit/pressable'
import { ChevronDownIcon, CheckCircleIcon } from '@components/kit/icons'

import {
  getOpportunityDetailTitle,
  getOpportunityDetailValue,
  shouldRenderOpportunityDetail
} from '@utils/opportunity'
import {
  IOpportunityDetails,
  IOpportunityDetailsQuestion,
  OpportunityStatus
} from '@store/Opportunity/types'
import { ChatContext } from '../chat.web'
import { useOpportunityStore } from '@store/Opportunity'
import PostItCard from '@components/postItCard'
import { FormButtonPanel, TextFieldFormated } from '@components/formFields'
import { accessibilityLabel } from '@components/kit/_support'

const RETRACTED_PAGE_SIZE = 3

interface IAddQuestionModal {
  isOpen: boolean
  closeModal: () => void
  addQuestion: (
    question: IOpportunityDetailsQuestion
  ) => Promise<{ success: boolean; errorMessage?: string }>
}

function AddQuestionModal({
  isOpen,
  closeModal,
  addQuestion
}: IAddQuestionModal) {
  const [text, setText] = useState('')
  const [description, setDescription] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const handleClose = () => {
    setText('')
    setDescription('')
    setErrorMessage('')
    closeModal()
  }

  const handleSave = async () => {
    if (text.trim()) {
      const question: IOpportunityDetailsQuestion = {
        text: text.trim(),
        description: description.trim()
      }
      const result = await addQuestion(question)
      if (result.success) {
        handleClose()
      } else {
        setErrorMessage(result.errorMessage || 'Failed to add question')
      }
    } else {
      setErrorMessage('Title cannot be empty')
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setText('')
      setDescription('')
      setErrorMessage('')
    }
  }, [isOpen])

  return (
    <Box width='30vw'>
      <Formik initialValues={{}} onSubmit={() => {}} validateOnChange={true}>
        <Form>
          <Box flex='1 1 auto' flexDirection='column' gap='08'>
            <Text font='display.20.bold' textAlign='left' marginBottom='16'>
              Add Question for Member
            </Text>
            <TextFieldFormated
              name='title'
              label='Title'
              value={text}
              onChangeText={setText}
              placeholder='Enter question title'
            />
            <TextFieldFormated
              name='description'
              label='Description'
              value={description}
              onChangeText={setDescription}
              placeholder='Enter question description'
              numberOfLines={3}
              height='4.5em'
              multiline
            />
            <FormButtonPanel onCancel={handleClose} onSave={handleSave} />
          </Box>
        </Form>
      </Formik>
      <Modal
        open={!!errorMessage}
        onRequestClose={() => {}}
        showCloseButton={false}
        borderRadius='medium'
      >
        <Box gap='20'>
          <Text font='display.20.bold' textAlign='center'>
            Error Adding Question
          </Text>
          <Text font='display.16.bold' textAlign='center' textColor='error'>
            {errorMessage}
          </Text>
          <Box flex='1 1 auto' alignItems='center'>
            <Button
              variant='secondary'
              size='medium'
              onPress={() => setErrorMessage('')}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}

interface IOpportunitiesDetailsModal {
  opportunityDetails: IOpportunityDetails
  setIsClosed: (isClosed: boolean) => void
}

function OpportunitiesDetailsModal({
  opportunityDetails,
  setIsClosed
}: IOpportunitiesDetailsModal) {
  return (
    <Box flex='1 1 auto' flexDirection='column' gap='08'>
      <ScrollView style={{ maxHeight: '85vh' }}>
        {!!opportunityDetails ? (
          Object.keys(opportunityDetails).map((key, index) => {
            if (!shouldRenderOpportunityDetail(key)) {
              return null
            }
            const isArray = Array.isArray(
              getOpportunityDetailValue(key, opportunityDetails[key])
            )
            return (
              <Box flexDirection='row' marginTop='08'>
                <Text
                  key={index}
                  font='display.16.bold'
                  textColor='black'
                  textTransform='capitalize'
                >
                  {`${getOpportunityDetailTitle(key)}:`}
                </Text>
                {isArray ? (
                  <Box flex='1 1 auto' marginLeft='08'>
                    {(opportunityDetails[key] as any[]).map(
                      (item, itemIndex) => (
                        <Text
                          key={itemIndex}
                          font='text.16.medium'
                          textColor='black'
                        >
                          {typeof item === 'string'
                            ? ` ${item}`
                            : ` ${item.text}: ${item.description}`}
                        </Text>
                      )
                    )}
                  </Box>
                ) : (
                  <Text font='text.16.medium' textColor='black'>
                    {` ${getOpportunityDetailValue(
                      key,
                      opportunityDetails[key]
                    )}`}
                  </Text>
                )}
              </Box>
            )
          })
        ) : (
          <Box marginTop='32'>
            <Loading size='medium' />
          </Box>
        )}
      </ScrollView>
      <Button
        variant='paleGreen'
        size='small'
        onPress={() => setIsClosed(false)}
      >
        Close
      </Button>
    </Box>
  )
}

interface OpportunitiesReviewCardProps {
  opportunity: IOpportunityDetails
  openDetailsModal: (opportunity: IOpportunityDetails) => void
  setIsSubmitting?: (isSubmitting: boolean) => void
}

function OpportunitiesReviewCard({
  opportunity,
  openDetailsModal,
  setIsSubmitting
}: OpportunitiesReviewCardProps) {
  const { userId } = useContext(ChatContext)
  const [isAddQuestionModalOpen, setIsAddQuestionModalOpen] = useState(false)
  const [memberQuestions, setMemberQuestions] = useState<
    IOpportunityDetailsQuestion[]
  >([])

  const [allQuestions, setAllQuestions] = useState<
    IOpportunityDetailsQuestion[]
  >([])

  const navigate = useNavigate()
  const {
    getRuleQuestionsByUserId,
    setRuleQuestionsByUserId,
    approveOpportunity,
    rejectOpportunity,
    activateOpportunityByUserId,
    deactivateOpportunityByUserId
  } = useOpportunityStore.getState()

  const addQuestion = async (question: IOpportunityDetailsQuestion) => {
    const updatedQuestions = [...memberQuestions, question]
    const result = await setRuleQuestionsByUserId(
      opportunity.rule_name,
      userId,
      updatedQuestions
    )
    if (result.success) {
      setMemberQuestions(updatedQuestions)
    }
    return result
  }

  const openAddQuestionModal = () => {
    setIsAddQuestionModalOpen(true)
  }
  const closeAddQuestionModal = () => {
    setIsAddQuestionModalOpen(false)
  }

  const handleApproveOpportunity = async () => {
    setIsSubmitting(true)
    await approveOpportunity(opportunity.id.toString())
    setIsSubmitting(false)
  }

  const handleRejectOpportunity = async () => {
    setIsSubmitting(true)
    await rejectOpportunity(opportunity.id.toString())
    setIsSubmitting(false)
  }

  const getPostItCardVariant = (opportunity: IOpportunityDetails) => {
    if (opportunity.status === OpportunityStatus.DEFAULT) {
      return 'disabled'
    }
    if (opportunity.status === OpportunityStatus.APPROVED) {
      return 'enabled'
    }
    if (opportunity.status === OpportunityStatus.REJECTED) {
      return 'rejected'
    }
    if (opportunity.status === OpportunityStatus.ACTIVATED) {
      return 'activated'
    }
  }
  const handleActivateOpportunity = () => {
    setIsSubmitting(true)
    activateOpportunityByUserId(userId, opportunity.rule_name).then(() => {
      setIsSubmitting(false)
    })
  }

  const handleDeactivateOpportunity = () => {
    setIsSubmitting(true)
    deactivateOpportunityByUserId(userId, opportunity.rule_name).then(() => {
      setIsSubmitting(false)
    })
  }

  const isDefault = opportunity.status === OpportunityStatus.DEFAULT
  const isApproved = opportunity.status === OpportunityStatus.APPROVED
  const isDefined = opportunity.questions.length > 0
  const isActivated = opportunity.status === OpportunityStatus.ACTIVATED

  const shouldRenderButtonPanel =
    opportunity.status !== OpportunityStatus.REJECTED

  useEffect(() => {
    if ((isActivated || isApproved) && isDefined) {
      getRuleQuestionsByUserId(opportunity.rule_name, userId).then(
        questions => {
          if (questions) {
            setMemberQuestions(questions)
          } else {
            console.error('Error fetching member questions')
          }
        }
      )
    }
  }, [
    opportunity.rule_name,
    userId,
    getRuleQuestionsByUserId,
    isDefined,
    isActivated,
    isApproved
  ])

  //joins member questions with opportunity questions
  useEffect(() => {
    if ((isActivated || isApproved) && isDefined) {
      const allQuestions = [...opportunity.questions, ...memberQuestions]
      setAllQuestions(allQuestions)
    }
  }, [
    opportunity.questions,
    memberQuestions,
    isDefined,
    isActivated,
    isApproved
  ])

  return (
    <PostItCard
      key={opportunity.id}
      variant={getPostItCardVariant(opportunity)}
    >
      <Box flex='1 1 auto' flexDirection='column' gap='08'>
        {isActivated && (
          <Box flexDirection='row' alignItems='center' gap='08'>
            <CheckCircleIcon />
            <Text font='text.14.semibold' textColor='green'>
              Displaying to member
            </Text>
          </Box>
        )}
        <Box flex='1 1 auto'>
          <Text font='text.14.semibold' textColor='purple'>
            {opportunity.rule_name}
          </Text>
        </Box>
        {(isActivated || isApproved) && isDefined && (
          <Box flex='1 1 auto' gap='08'>
            {allQuestions.map((question, index) => (
              <Box
                key={index}
                flexDirection='column'
                gap='08'
                backgroundColor={
                  isActivated ? 'white' : 'walrusNeutralSecondary'
                }
                padding='08'
                borderRadius='medium'
              >
                <Text font='text.14.semibold' textColor='walrusBlue200'>
                  {question.text}
                </Text>
                {question.description && (
                  <Text
                    font='text.14.medium'
                    textColor='walrusBlue700'
                    marginLeft='08'
                  >
                    {question.description}
                  </Text>
                )}
              </Box>
            ))}
            <Box justifyContent='center' alignItems='center' marginBottom='08'>
              <Button
                variant='tertiary'
                size='fit'
                backgroundColor='transparent'
                onPress={() => openAddQuestionModal()}
              >
                + Add Question
              </Button>
            </Box>
          </Box>
        )}
        {shouldRenderButtonPanel && (
          <Box
            flex='1 1 auto'
            width='100%'
            flexDirection='row'
            alignItems='flex-start'
            justifyContent='space-between'
          >
            <Box flexDirection='row' gap='08' marginRight='08'>
              <Button
                variant='tertiary'
                size='small'
                onPress={() => openDetailsModal(opportunity)}
              >
                Details
              </Button>
            </Box>
            <Box alignItems='center'>
              <Box flexDirection='row' gap='08'>
                {isDefault && (
                  <>
                    <Button
                      variant='paleGreen'
                      size='small'
                      onPress={handleApproveOpportunity}
                    >
                      Approve
                    </Button>
                    <Button
                      variant='paleRed'
                      size='small'
                      onPress={handleRejectOpportunity}
                    >
                      Reject
                    </Button>
                  </>
                )}
                {isApproved && !isDefined && (
                  <Button
                    variant='punchThroughViolet'
                    size='small'
                    onPress={() =>
                      navigate(`/opportunities/unique/${opportunity.rule_name}`)
                    }
                    textDecorationLine='underline'
                    backgroundColor='transparent'
                  >
                    Define Opportunity Copy
                  </Button>
                )}
                {isApproved && isDefined && (
                  <Button
                    variant='paleGreen'
                    size='small'
                    onPress={handleActivateOpportunity}
                  >
                    Activate
                  </Button>
                )}
                {isActivated && (
                  <Button
                    variant='paleRed'
                    size='small'
                    onPress={handleDeactivateOpportunity}
                  >
                    Deactivate
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <Modal
        open={isAddQuestionModalOpen}
        onRequestClose={closeAddQuestionModal}
        showCloseButton={true}
        borderRadius='medium'
      >
        <AddQuestionModal
          isOpen={isAddQuestionModalOpen}
          closeModal={closeAddQuestionModal}
          addQuestion={addQuestion}
        />
      </Modal>
    </PostItCard>
  )
}

function OpportunitiesReview() {
  const { userId } = useContext(ChatContext)
  const [opportunities, setOpportunities] = useState<IOpportunityDetails[]>([])
  const { getOpportunitiesByUserId } = useOpportunityStore.getState()
  const [isLoading, setIsLoading] = useState(true)
  const [shouldReload, setShouldReload] = useState(true)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedOpportunity, setSelectedOpportunity] =
    useState<IOpportunityDetails | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const isRetractable = opportunities.length > RETRACTED_PAGE_SIZE

  const openDetailsModal = (opportunity: IOpportunityDetails) => {
    setSelectedOpportunity(opportunity)
    setIsDetailsModalOpen(true)
  }

  const setIsSubmitting = (isSubmitting: boolean) => {
    setIsLoading(isSubmitting)
    if (!isSubmitting) {
      setShouldReload(true)
    }
  }

  useEffect(() => {
    if (shouldReload) {
      setIsLoading(true)
      getOpportunitiesByUserId(userId)
        .then(response => {
          if (response) {
            // ORDER: 1st ACTIVATED, 2nd APPROVED with questions, 3rd APPROVED without questions, 4rd DEFAULT, 5th REJECTED
            const orderedResponse = response.sort((a, b) => {
              if (a.status === OpportunityStatus.ACTIVATED) return -1
              if (b.status === OpportunityStatus.ACTIVATED) return 1
              if (
                a.status === OpportunityStatus.APPROVED &&
                a.questions.length > 0
              )
                return -1
              if (
                b.status === OpportunityStatus.APPROVED &&
                b.questions.length > 0
              )
                return 1
              if (a.status === OpportunityStatus.APPROVED) return -1
              if (b.status === OpportunityStatus.APPROVED) return 1
              if (a.status === OpportunityStatus.DEFAULT) return -1
              if (b.status === OpportunityStatus.DEFAULT) return 1
              return 0
            })
            setOpportunities(orderedResponse)
          } else {
            console.error('Error fetching opportunities')
          }
        })
        .finally(() => {
          setIsLoading(false)
          setShouldReload(false)
        })
    }
  }, [userId, getOpportunitiesByUserId, shouldReload])

  return (
    <Box pt='24' {...accessibilityLabel('OpportunitiesReview')}>
      <Text ml='12' mb='08' font='display.16.bold'>
        Opportunities Review
      </Text>
      {opportunities?.length === 0 && !isLoading && (
        <Box
          flex='1 1 auto'
          justifyContent='center'
          alignItems='center'
          padding='16'
        >
          <Text font='text.16.medium' textColor='black'>
            No opportunities available for review.
          </Text>
        </Box>
      )}
      <Box px='00' py='00' gap='16'>
        {!!opportunities?.length &&
          opportunities.map((opportunity, index) => {
            if (isRetractable && !isExpanded && index >= RETRACTED_PAGE_SIZE) {
              return null
            }
            return (
              <OpportunitiesReviewCard
                key={index}
                opportunity={opportunity}
                setIsSubmitting={setIsSubmitting}
                openDetailsModal={() => openDetailsModal(opportunity)}
              />
            )
          })}
        {isRetractable && (
          <Box alignItems='center' justifyContent='center'>
            <Pressable
              onPress={() => setIsExpanded(!isExpanded)}
              justifyContent='center'
              alignItems='center'
            >
              <Text font='text.14.semibold' textColor='purple'>
                {isExpanded ? 'Show Less' : 'Show More'}
              </Text>
              <Box
                style={{
                  transform: [{ rotate: !isExpanded ? '0deg' : '180deg' }]
                }}
                width='20'
                height='20'
                justifyContent='center'
                alignItems='center'
              >
                <ChevronDownIcon fill={'shade07'} width={20} height={20} />
              </Box>
            </Pressable>
          </Box>
        )}
        {isLoading && (
          <Box
            position='absolute'
            width='100%'
            height='100%'
            top='0'
            left='0'
            right='0'
            flex='1'
            justifyContent='center'
            alignItems='center'
            style={{
              backgroundColor: '#f4f7facc'
            }}
          >
            <Loading size='large' />
          </Box>
        )}
      </Box>
      <Modal
        open={isDetailsModalOpen}
        onRequestClose={() => setIsDetailsModalOpen(false)}
        showCloseButton={true}
        borderRadius='medium'
        paddingTop='40'
      >
        <OpportunitiesDetailsModal
          opportunityDetails={selectedOpportunity}
          setIsClosed={setIsDetailsModalOpen}
        />
      </Modal>
    </Box>
  )
}

export default OpportunitiesReview
